import jsPDF from 'jspdf';

export const generateLabInvoicePDF = async (invoiceData, patientInfo = null, officeData = null) => {
    const pdf = new jsPDF('p', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const lineHeight = 7;
    let yPosition = margin;

    // Header
    pdf.setFontSize(24);
    pdf.setTextColor(41, 128, 185);
    pdf.text('LAB INVOICE', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight * 2.5;

    // Two-column layout for Facility and Patient Information
    const leftColumnX = margin;
    const rightColumnX = pageWidth / 2 + 10;
    const columnWidth = (pageWidth - margin * 2 - 20) / 2;
    const sectionStartY = yPosition;

    // Facility Information (Left Column)
    if (officeData) {
        pdf.setFontSize(12);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Facility Information', leftColumnX, yPosition);
        let facilityY = yPosition + lineHeight;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);

        // Helper function to check if data should be displayed (matching ClinicInformation.js)
        const shouldDisplay = (data) => Array.isArray(data) && data[1] === true;

        // Facility Name (always show if available)
        if (officeData.facility_name && officeData.facility_name[0]) {
            pdf.text(`Facility Name: ${officeData.facility_name[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Practice Speciality
        if (shouldDisplay(officeData.speciality_type)) {
            pdf.text(`Practice Speciality: ${officeData.speciality_type[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Facility Address
        if (shouldDisplay(officeData.facility_address)) {
            const addressLines = pdf.splitTextToSize(`Facility Address: ${officeData.facility_address[0]}`, columnWidth);
            addressLines.forEach(line => {
                pdf.text(line, leftColumnX, facilityY);
                facilityY += lineHeight * 0.8;
            });
        }

        // City
        if (shouldDisplay(officeData.city)) {
            pdf.text(`City: ${officeData.city[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // State
        if (shouldDisplay(officeData.state)) {
            pdf.text(`State: ${officeData.state[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Country
        if (shouldDisplay(officeData.country)) {
            pdf.text(`Country: ${officeData.country[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Zip Code
        if (shouldDisplay(officeData.zipcode)) {
            pdf.text(`Zip Code: ${officeData.zipcode[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Office Phone
        if (shouldDisplay(officeData.office_phone)) {
            pdf.text(`Office Phone: ${officeData.office_phone[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Office Fax
        if (shouldDisplay(officeData.office_fax)) {
            pdf.text(`Office Fax: ${officeData.office_fax[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Business Email
        if (shouldDisplay(officeData.buisness_email)) {
            pdf.text(`Business Email: ${officeData.buisness_email[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Additional Basic Data 1
        if (shouldDisplay(officeData.additional_basic_data_1)) {
            pdf.text(`Data I: ${officeData.additional_basic_data_1[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Additional Basic Data 2
        if (shouldDisplay(officeData.additional_basic_data_2)) {
            pdf.text(`Data II: ${officeData.additional_basic_data_2[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Additional Basic Data 3
        if (shouldDisplay(officeData.additional_basic_data_3)) {
            pdf.text(`Data III: ${officeData.additional_basic_data_3[0]}`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;
        }

        // Exam Rooms
        if (shouldDisplay(officeData.exam_rooms)) {
            pdf.text(`Exam Rooms:`, leftColumnX, facilityY);
            facilityY += lineHeight * 0.8;

            Object.entries(officeData.exam_rooms).forEach(([key, value]) => {
                const roomText = `  ${key}: ${value}`;
                const roomLines = pdf.splitTextToSize(roomText, columnWidth - 10);
                roomLines.forEach(line => {
                    pdf.text(line, leftColumnX + 5, facilityY);
                    facilityY += lineHeight * 0.7;
                });
            });
        }

        // Operating Hours
        if (shouldDisplay(officeData.operating_hrs)) {
            const operatingHours = officeData.operating_hrs[0];
            const hoursText = `Operating Hours: ${operatingHours.start_time} to ${operatingHours.end_time}`;
            const hoursLines = pdf.splitTextToSize(hoursText, columnWidth);
            hoursLines.forEach(line => {
                pdf.text(line, leftColumnX, facilityY);
                facilityY += lineHeight * 0.8;
            });
        }

        yPosition = Math.max(yPosition, facilityY);
    }

    // Patient Information (Right Column)
    if (patientInfo) {
        let patientY = sectionStartY;

        pdf.setFontSize(12);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Patient Information', rightColumnX, patientY);
        patientY += lineHeight;

        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);

        // Patient Name
        const patientName = patientInfo.first_name && patientInfo.last_name
            ? `${patientInfo.first_name} ${patientInfo.last_name}`
            : 'N/A';
        pdf.text(`Name: ${patientName}`, rightColumnX, patientY);
        patientY += lineHeight * 0.8;

        // Date of Birth and Age
        if (patientInfo.date_of_birth) {
            const dob = new Date(patientInfo.date_of_birth).toLocaleDateString();
            pdf.text(`DOB: ${dob}`, rightColumnX, patientY);
            patientY += lineHeight * 0.8;

            // Calculate age
            const today = new Date();
            const birthDate = new Date(patientInfo.date_of_birth);
            const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000));
            pdf.text(`Age: ${age} y/o`, rightColumnX, patientY);
            patientY += lineHeight * 0.8;
        }

        // Gender
        if (patientInfo.gender) {
            pdf.text(`Gender: ${patientInfo.gender}`, rightColumnX, patientY);
            patientY += lineHeight * 0.8;
        }

        // Patient ID
        if (patientInfo.id) {
            pdf.text(`Patient ID: ${patientInfo.id}`, rightColumnX, patientY);
            patientY += lineHeight * 0.8;
        }

        // Address
        const addressParts = [];
        if (patientInfo.address) addressParts.push(patientInfo.address);
        if (patientInfo.city) addressParts.push(patientInfo.city);
        if (patientInfo.state) addressParts.push(patientInfo.state);
        if (patientInfo.zipcode) addressParts.push(patientInfo.zipcode);

        if (addressParts.length > 0) {
            const addressLines = pdf.splitTextToSize(`Address: ${addressParts.join(', ')}`, columnWidth);
            addressLines.forEach(line => {
                pdf.text(line, rightColumnX, patientY);
                patientY += lineHeight * 0.8;
            });
        }

        // Phone
        if (patientInfo.patient_phone) {
            pdf.text(`Phone: ${patientInfo.patient_phone}`, rightColumnX, patientY);
            patientY += lineHeight * 0.8;
        }

        // Add invoice information under patient information
        if (invoiceData) {
            patientY += lineHeight * 0.5;

            // Draw a subtle separator
            pdf.setDrawColor(220, 220, 220);
            pdf.line(rightColumnX, patientY, rightColumnX + columnWidth, patientY);
            patientY += lineHeight;

            pdf.setFontSize(10);
            pdf.setTextColor(52, 73, 94);
            pdf.text('Invoice Information', rightColumnX, patientY);
            patientY += lineHeight;

            pdf.setFontSize(9);
            pdf.setTextColor(0, 0, 0);

            // Invoice ID and Status
            if (invoiceData.inhouseUID) {
                pdf.text(`Invoice ID: ${invoiceData.inhouseUID}`, rightColumnX, patientY);
                patientY += lineHeight * 0.8;
            }

            if (invoiceData.status) {
                const statusText = invoiceData.status.charAt(0).toUpperCase() + invoiceData.status.slice(1);
                pdf.text(`Status: ${statusText}`, rightColumnX, patientY);
                patientY += lineHeight * 0.8;
            }

            // Issue Date
            if (invoiceData.issue_date) {
                const issueDate = new Date(invoiceData.issue_date).toLocaleDateString();
                const issueTime = new Date(invoiceData.issue_date).toLocaleTimeString();
                pdf.text(`Issue Date: ${issueDate} ${issueTime}`, rightColumnX, patientY);
                patientY += lineHeight * 0.8;
            }

            // Added By
            if (invoiceData.added_by) {
                pdf.text(`Added By: ${invoiceData.added_by}`, rightColumnX, patientY);
                patientY += lineHeight * 0.8;
            }





        }

        yPosition = Math.max(yPosition, patientY);
    }

    yPosition += lineHeight * 1.5;

    // Draw separator line
    pdf.setDrawColor(52, 73, 94);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += lineHeight * 1.5;

    // Lab Services Section
    if (invoiceData) {
        // Check if we need a new page
        if (yPosition > pageHeight - 120) {
            pdf.addPage();
            yPosition = margin;
        }

        // Determine currency for the entire invoice - use office settings if available, fallback to invoice data or default
        const currency = officeData?.local_currency || invoiceData.currency || 'USD';
        const currencySymbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'PKR' ? 'PKR ' : currency === 'GBP' ? '£' : currency === 'JPY' ? '¥' : currency === 'CAD' ? 'C$' : currency === 'AUD' ? 'A$' : `${currency} `;

        // Laboratory Tests/Services
        pdf.setFontSize(12);
        pdf.setTextColor(52, 73, 94);
        pdf.text('Laboratory Services:', margin, yPosition);
        yPosition += lineHeight;

        // Handle lab_name array
        const labs = invoiceData.lab_name || [];
        let totalAmount = 0;

        if (labs.length > 0) {
            // Table header
            pdf.setFontSize(9);
            pdf.setTextColor(255, 255, 255);
            pdf.setFillColor(52, 73, 94);
            pdf.rect(margin, yPosition, pageWidth - margin * 2, lineHeight, 'F');

            pdf.text('#', margin + 2, yPosition + lineHeight * 0.7);
            pdf.text('Lab Details', margin + 15, yPosition + lineHeight * 0.7);
            pdf.text('Price', pageWidth - margin - 30, yPosition + lineHeight * 0.7);
            yPosition += lineHeight;

            pdf.setTextColor(0, 0, 0);

            labs.forEach((lab, labIndex) => {
                // Check if we need a new page
                if (yPosition > pageHeight - 80) {
                    pdf.addPage();
                    yPosition = margin;
                }

                // Alternate row colors for the entire lab section
                const sectionStartY = yPosition;
                let sectionHeight = lineHeight * 4; // Estimate height needed

                if (labIndex % 2 === 1) {
                    pdf.setFillColor(248, 249, 250);
                    pdf.rect(margin, yPosition, pageWidth - margin * 2, sectionHeight, 'F');
                }

                // Row number and Lab Name
                pdf.setFontSize(10);
                pdf.setTextColor(52, 73, 94);
                pdf.text(`${labIndex + 1}.`, margin + 2, yPosition + lineHeight * 0.7);

                const labName = lab.lab_name || 'Unknown Lab';
                pdf.text(`${labName}`, margin + 15, yPosition + lineHeight * 0.7);

                // Price on the right
                const price = parseFloat(lab.lab_price) || 0;
                totalAmount += price;
                pdf.text(`${currencySymbol}${price.toFixed(2)}`, pageWidth - margin - 50, yPosition + lineHeight * 0.7);
                yPosition += lineHeight;



                // Reset colors and add spacing
                pdf.setFontSize(9);
                pdf.setTextColor(0, 0, 0);
                yPosition += lineHeight * 0.5;
            });

            // Invoice Summary Section
            yPosition += lineHeight * 0.5;
            pdf.setDrawColor(220, 220, 220);
            pdf.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += lineHeight;

            // Use actual invoice data values exactly as shown in ViewLabInvoice
            const subtotal = invoiceData.total_price ? parseFloat(invoiceData.total_price) : totalAmount;
            const discountPercentage = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
            const discountAmount = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
            const taxAmount = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
            const grandTotal = invoiceData.grand_total ? parseFloat(invoiceData.grand_total) : subtotal;

            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);

            // Sub Total
            pdf.text('Sub Total:', pageWidth - margin - 100, yPosition);
            pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
            yPosition += lineHeight;

            // Discount (only show if there's actual discount data)
            if (discountAmount && discountAmount > 0) {
                pdf.setTextColor(220, 53, 69); // Red color for discount
                const discountText = discountPercentage ? `Discount ${discountPercentage}%:` : 'Discount:';
                pdf.text(discountText, pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${discountAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                pdf.setTextColor(0, 0, 0); // Reset color
                yPosition += lineHeight;
            }

            // Tax (only show if there's actual tax data)
            if (taxAmount && taxAmount > 0) {
                pdf.text('Tax:', pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${taxAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                yPosition += lineHeight;
            }

            // Draw line before grand total
            pdf.setDrawColor(52, 73, 94);
            pdf.line(pageWidth - margin - 120, yPosition, pageWidth - margin, yPosition);
            yPosition += lineHeight;

            // Grand Total
            pdf.setFontSize(12);
            pdf.setTextColor(52, 73, 94);
            pdf.text('Grand Total:', pageWidth - margin - 100, yPosition);
            pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
            yPosition += lineHeight * 1.5;

        } else {
            pdf.setFontSize(9);
            pdf.setTextColor(128, 128, 128);
            pdf.text('No laboratory services specified', margin + 5, yPosition);
            yPosition += lineHeight;

            // Display detailed breakdown even without lab details if total price is available
            if (invoiceData.total_price) {
                yPosition += lineHeight;

                // Draw separator line
                pdf.setDrawColor(220, 220, 220);
                pdf.line(margin, yPosition, pageWidth - margin, yPosition);
                yPosition += lineHeight;

                // Use actual invoice data values exactly as shown in ViewLabInvoice
                const subtotal = invoiceData.total_price ? parseFloat(invoiceData.total_price) : 0;
                const discountPercentage = invoiceData.discount ? parseFloat(invoiceData.discount) : null;
                const discountAmount = invoiceData.discounted_amount ? parseFloat(invoiceData.discounted_amount) : null;
                const taxAmount = invoiceData.tax ? parseFloat(invoiceData.tax) : null;
                const grandTotal = invoiceData.grand_total ? parseFloat(invoiceData.grand_total) : subtotal;

                pdf.setFontSize(10);
                pdf.setTextColor(0, 0, 0);

                // Sub Total
                pdf.text('Sub Total:', pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${subtotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                yPosition += lineHeight;

                // Discount (only show if there's actual discount data)
                if (discountAmount && discountAmount > 0) {
                    pdf.setTextColor(220, 53, 69);
                    const discountText = discountPercentage ? `Discount ${discountPercentage}%:` : 'Discount:';
                    pdf.text(discountText, pageWidth - margin - 100, yPosition);
                    pdf.text(`${currencySymbol}${discountAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                    pdf.setTextColor(0, 0, 0); // Reset color
                    yPosition += lineHeight;
                }

                // Tax (only show if there's actual tax data)
                if (taxAmount && taxAmount > 0) {
                    pdf.text('Tax:', pageWidth - margin - 100, yPosition);
                    pdf.text(`${currencySymbol}${taxAmount.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                    yPosition += lineHeight;
                }

                // Draw line before grand total
                pdf.setDrawColor(52, 73, 94);
                pdf.line(pageWidth - margin - 120, yPosition, pageWidth - margin, yPosition);
                yPosition += lineHeight;

                // Grand Total
                pdf.setFontSize(12);
                pdf.setTextColor(52, 73, 94);
                pdf.text('Grand Total:', pageWidth - margin - 100, yPosition);
                pdf.text(`${currencySymbol}${grandTotal.toFixed(2)}`, pageWidth - margin - 30, yPosition, { align: 'right' });
                yPosition += lineHeight;
            }
        }

    }

    // Add page numbers
    const pageCount = pdf.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    // Save the PDF
    const dateStamp = new Date().toISOString().split('T')[0];
    const invoiceId = invoiceData?.inhouseUID || 'Unknown';
    const patientName = patientInfo?.first_name && patientInfo?.last_name
        ? `${patientInfo.first_name}_${patientInfo.last_name}`
        : 'Patient';
    const fileName = `Lab_Invoice_${invoiceId}_${patientName}_${dateStamp}.pdf`;

    pdf.save(fileName);
};
